import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../lib/i18n";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { t } = useI18n();
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/mypage";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("auth.success_login"));
      nav(next);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-6 py-16 lg:py-24">
      <div className="mb-10">
        <div className="label-mono mb-2">// Welcome back</div>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight uppercase text-white caret">
          {t("auth.login_title")}
        </h1>
      </div>
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div>
          <label className="block label-mono mb-2">{t("auth.email")}</label>
          <input
            data-testid="login-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full bg-[#0A0E1A] border border-[#1E2A3F] focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6] outline-none p-3 text-base text-white font-mono"
          />
        </div>
        <div>
          <label className="block label-mono mb-2">{t("auth.password")}</label>
          <input
            data-testid="login-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            className="w-full bg-[#0A0E1A] border border-[#1E2A3F] focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6] outline-none p-3 text-base text-white font-mono"
          />
        </div>
        <button
          disabled={loading}
          data-testid="login-submit-button"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#3B82F6] text-black font-mono text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#60A5FA] disabled:opacity-60 neon-glow-soft"
        >
          {loading ? "…" : t("auth.login_btn")}
          <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </button>
      </form>
      <div className="mt-8 text-sm">
        <Link
          to="/register"
          data-testid="login-goto-register-link"
          className="text-[#3B82F6] hover:underline font-mono text-[11px] uppercase tracking-[0.18em]"
        >
          {t("auth.go_register")} →
        </Link>
      </div>
      <p className="mt-10 text-xs text-[#64748B] leading-relaxed border-t border-[#1E2A3F] pt-6">
        {t("auth.anon_note")}
      </p>
    </div>
  );
}
